import React, { useState, useEffect, useCallback, Suspense } from 'react';
import Taskbar from './components/Taskbar';
import StartMenu from './components/StartMenu';
import DesktopIcon from './components/DesktopIcon';
import Window from './components/Window';
import { apps } from './config/apps';
import DesktopBackground from './components/DesktopBackground';
import ArtworkDetailWindow from './windows/ArtworkDetailWindow';
import BlogDetailWindow from './windows/BlogDetailWindow';
import BootWindow from './windows/BootWindow';
import DesktopPet from './components/DesktopPet';
import { artworks } from './data/artworks';
import { blogPosts } from './data/blog';
import blogIcon from './assets/icons/blogicon.png';
import { useWindowManager } from './hooks/useWindowManager';
import { useDesktopIconLayout } from './hooks/useDesktopIconLayout';
import { getWindowTheme } from './config/windowTheme';
import SpotifyWidget from './components/SpotifyWidget';
import { useMobileViewport } from './hooks/useMobileViewport';

/** Minimal fallback shown while a lazy window chunk loads */
const WindowLoadingFallback: React.FC<{ isDarkMode: boolean }> = ({ isDarkMode }) => {
  const t = getWindowTheme(isDarkMode);
  return (
  <div
    className={`flex items-center justify-center h-full text-sm ${t.bg} ${t.muted}`}
  >
    Loading…
  </div>
  );
};

const App: React.FC = () => {
  const [isBooting, setIsBooting] = useState(true);
  const [isStartMenuOpen, setIsStartMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isDarkMode, setIsDarkMode] = useState(false);
  const isMobile = useMobileViewport();
  const [isSpotifyWidgetOpen, setIsSpotifyWidgetOpen] = useState(true);
  const [spotifyMobileExpanded, setSpotifyMobileExpanded] = useState(false);
  const iconLayout = useDesktopIconLayout(apps.length);

  const {
    openWindows,
    minimizedWindows,
    activeWindow,
    windowOrder,
    windowOpenIndex,
    openWindow,
    closeWindow,
    minimizeWindow,
    focusWindow,
    restoreAllWindows,
  } = useWindowManager('guide');

  // Boot sequence
  useEffect(() => {
    const timer = setTimeout(() => setIsBooting(false), 2800);
    return () => clearTimeout(timer);
  }, []);

  // Clock
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const handleAppClick = useCallback(
    (appId: string) => {
      openWindow(appId);
      setIsStartMenuOpen(false);
    },
    [openWindow],
  );

  const handleToggleDarkMode = useCallback(() => setIsDarkMode(prev => !prev), []);

  const handleSpotifyWidgetToggle = useCallback(() => {
    if (isMobile) {
      setSpotifyMobileExpanded(prev => !prev);
    } else {
      setIsSpotifyWidgetOpen(prev => !prev);
    }
  }, [isMobile]);

  return (
    <div
      className="h-screen w-screen overflow-hidden relative"
      style={{
        background: isDarkMode ? '#09070f' : '#f5f3f0',
        transition: 'background 0.8s ease-in-out',
      }}
    >
      {isBooting ? (
        <BootWindow />
      ) : (
        <>
          {/* Background */}
          <div className="absolute inset-0 z-0">
            <DesktopBackground isDarkMode={isDarkMode} />

            {/* Dark overlay */}
            <div
              className="absolute inset-0 bg-black pointer-events-none"
              style={{
                opacity: isDarkMode ? 0.28 : 0,
                transition: 'opacity 0.8s ease-in-out',
                zIndex: 2,
              }}
            />

            {/* Light ambient glow */}
            <div
              className="absolute top-[20%] right-[10%] w-96 h-96 rounded-full bg-pink-200 blur-3xl pointer-events-none"
              style={{ opacity: isDarkMode ? 0 : 0.5, transition: 'opacity 0.8s ease-in-out', zIndex: 2 }}
            />
          </div>

          {/* Desktop Icons — pinned above taskbar, columns reflow on resize */}
          <div
            className="absolute left-0 z-10 box-border pl-2 sm:pl-4 overflow-x-auto overflow-y-hidden overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            style={{
              top: iconLayout.topPadding,
              bottom: `calc(${iconLayout.taskbarHeight + iconLayout.bottomClearance}px + env(safe-area-inset-bottom, 0px))`,
              maxWidth: iconLayout.availableWidth,
            }}
          >
            <div
              className="grid w-max max-h-full content-start"
              style={{
                gridAutoFlow: 'column',
                gridTemplateRows: `repeat(${iconLayout.iconsPerColumn}, auto)`,
                gap: iconLayout.gap,
              }}
            >
              {apps.map(app => (
                <DesktopIcon
                  key={app.id}
                  icon={app.icon}
                  label={app.name}
                  onClick={() => handleAppClick(app.id)}
                  isDarkMode={isDarkMode}
                />
              ))}
            </div>
          </div>

          {/* Windows */}
          {Object.entries(openWindows).map(([appId, isOpen]) => {
            if (!isOpen) return null;

            const zIndex = windowOrder.indexOf(appId) + 10;
            const effectiveZIndex =
              minimizedWindows[appId] ? 0 : activeWindow === appId ? 1000 : zIndex;

            // Artwork detail windows
            if (appId.startsWith('artwork-')) {
              const artworkId = appId.replace(/^artwork-/, '');
              const artwork = artworks.find(a => a.id === artworkId);
              if (!artwork) return null;

              return (
                <Window
                  key={appId}
                  isOpen={isOpen}
                  title={artwork.title}
                  icon="🎨"
                  isMinimized={minimizedWindows[appId]}
                  isActive={activeWindow === appId}
                  onClose={() => closeWindow(appId)}
                  onMinimize={() => minimizeWindow(appId)}
                  onFocus={() => focusWindow(appId)}
                  appId={appId}
                  zIndex={effectiveZIndex}
                  isDarkMode={isDarkMode}
                  openIndex={windowOpenIndex[appId] ?? 0}
                >
                  <ArtworkDetailWindow artwork={artwork} isDarkMode={isDarkMode} />
                </Window>
              );
            }

            // Blog post detail windows
            if (appId.startsWith('blog-')) {
              const postId = appId.replace(/^blog-/, '');
              const post = blogPosts.find(p => p.id === postId);
              if (!post) return null;

              return (
                <Window
                  key={appId}
                  isOpen={isOpen}
                  title={post.title}
                  icon={blogIcon}
                  isMinimized={minimizedWindows[appId]}
                  isActive={activeWindow === appId}
                  onClose={() => closeWindow(appId)}
                  onMinimize={() => minimizeWindow(appId)}
                  onFocus={() => focusWindow(appId)}
                  appId={appId}
                  zIndex={effectiveZIndex}
                  isDarkMode={isDarkMode}
                  openIndex={windowOpenIndex[appId] ?? 0}
                >
                  <BlogDetailWindow post={post} isDarkMode={isDarkMode} />
                </Window>
              );
            }

            // Regular app windows
            const app = apps.find(a => a.id === appId);
            if (!app) return null;

            return (
              <Window
                key={appId}
                isOpen={isOpen}
                title={app.name}
                icon={app.icon}
                isMinimized={minimizedWindows[appId]}
                isActive={activeWindow === appId}
                onClose={() => closeWindow(appId)}
                onMinimize={() => minimizeWindow(appId)}
                onFocus={() => focusWindow(appId)}
                appId={appId}
                zIndex={effectiveZIndex}
                isDarkMode={isDarkMode}
                openIndex={windowOpenIndex[appId] ?? 0}
              >
                <Suspense fallback={<WindowLoadingFallback isDarkMode={isDarkMode} />}>
                  <app.component isDarkMode={isDarkMode} />
                </Suspense>
              </Window>
            );
          })}

          {/* Desktop Pet */}
          <DesktopPet isDarkMode={isDarkMode} />

          {/* Spotify Now Playing widget — always visible on mobile (minimized bar); toggle on desktop */}
          {(isMobile || isSpotifyWidgetOpen) && (
            <SpotifyWidget
              isDarkMode={isDarkMode}
              isExpanded={isMobile ? spotifyMobileExpanded : undefined}
              onExpandedChange={isMobile ? setSpotifyMobileExpanded : undefined}
            />
          )}

          {/* Taskbar */}
          <div className="fixed bottom-0 left-0 right-0 z-[9999]">
            <Taskbar
              isStartMenuOpen={isStartMenuOpen}
              onStartMenuToggle={() => setIsStartMenuOpen(!isStartMenuOpen)}
              isSpotifyWidgetOpen={isMobile ? spotifyMobileExpanded : isSpotifyWidgetOpen}
              onSpotifyWidgetToggle={handleSpotifyWidgetToggle}
              currentTime={currentTime}
              openWindows={openWindows}
              minimizedWindows={minimizedWindows}
              apps={apps}
              onAppClick={handleAppClick}
              onMinimizeWindow={minimizeWindow}
              onRestoreAllWindows={restoreAllWindows}
              isDarkMode={isDarkMode}
              onToggleDarkMode={handleToggleDarkMode}
            />
          </div>

          {/* Start Menu */}
          <StartMenu
            isOpen={isStartMenuOpen}
            onClose={() => setIsStartMenuOpen(false)}
            onAppClick={handleAppClick}
            isDarkMode={isDarkMode}
          />
        </>
      )}
    </div>
  );
};

export default App;
